import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import {
  LayoutDashboard, ArrowDownToLine, ArrowUpFromLine,
  Receipt, Settings, LogOut, Menu, X, Sparkles, Bell, User,
  Server, Route, Webhook, FileText, ChevronLeft, Building2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";
import { motion, AnimatePresence } from "framer-motion";

/* ══════════════════════════════════════════
   GLOBAL CSS — injected once, never changes
══════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Syne:wght@600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --rs-cyan:    #06b6d4;
  --rs-cyan4:   #22d3ee;
  --rs-violet:  #6366f1;
  --rs-violet4: #818cf8;
  --rs-emerald: #34d399;
  --rs-rose:    #fb7185;
  --rs-n950:    #040810;
  --rs-n900:    #060e1a;
  --rs-n850:    #08111e;
  --rs-b1:      rgba(6,182,212,0.08);
  --rs-b2:      rgba(6,182,212,0.18);
  --rs-t1:      #e8f4ff;
  --rs-t2:      #a8c4e0;
  --rs-t3:      #5a82a0;
  --rs-t4:      #2e4a66;
  --rs-t5:      #1a2e44;
  --rs-fd:      'Plus Jakarta Sans', sans-serif;
  --rs-fs:      'Syne', sans-serif;
  --rs-fm:      'Geist Mono', monospace;
  --rs-out:     cubic-bezier(0.22,1,0.36,1);
  --rs-spring:  cubic-bezier(0.34,1.56,0.64,1);
}

html { scroll-behavior: smooth; }
body { background: var(--rs-n950); color: var(--rs-t1); font-family: var(--rs-fd); -webkit-font-smoothing: antialiased; }
*::-webkit-scrollbar { width: 3px; height: 3px; }
*::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.15); border-radius: 99px; }
*::-webkit-scrollbar-track { background: transparent; }

/* ══ SIDEBAR ══ */
.rs-sb {
  position: fixed; top: 0; left: 0; bottom: 0;
  display: flex; flex-direction: column;
  background:
    radial-gradient(ellipse 140% 35% at 50% -1%, rgba(6,182,212,0.11), transparent 55%),
    linear-gradient(180deg, #05091a 0%, #040810 55%, #030710 100%);
  border-right: 1px solid rgba(6,182,212,0.07);
  z-index: 50; overflow: hidden;
  transition: width 0.38s cubic-bezier(0.22,1,0.36,1);
  will-change: width;
}

/* Top glow bar */
.rs-sb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(6,182,212,0.7) 30%, rgba(99,102,241,0.7) 70%, transparent);
  z-index: 5; pointer-events: none;
  animation: rsSbglow 5s ease-in-out infinite;
}
@keyframes rsSbglow { 0%,100%{opacity:0.35} 50%{opacity:1} }

/* Bottom fade overlay */
.rs-sb::after {
  content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 90px;
  background: linear-gradient(to top, rgba(3,7,16,0.95), transparent);
  z-index: 4; pointer-events: none;
}

/* ── LOGO ── */
.rs-logo {
  height: 70px;
  display: flex; align-items: center;
  padding: 0 16px; gap: 13px;
  border-bottom: 1px solid rgba(6,182,212,0.07);
  flex-shrink: 0; text-decoration: none;
  position: relative; overflow: hidden; cursor: pointer;
  transition: background 0.22s;
  z-index: 2;
}
.rs-logo:hover { background: rgba(6,182,212,0.05); }

.rs-orb {
  width: 40px; height: 40px; border-radius: 13px;
  background: linear-gradient(135deg, #06b6d4, #6366f1);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; position: relative; overflow: hidden;
  box-shadow: 0 0 0 1px rgba(6,182,212,0.38), 0 4px 20px rgba(6,182,212,0.32);
  transition: box-shadow 0.3s, transform 0.4s var(--rs-spring);
}
.rs-orb::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 48%;
  background: rgba(255,255,255,0.22); border-radius: inherit;
}
.rs-orb svg { width: 18px; height: 18px; color: #fff; position: relative; z-index: 1; }
.rs-logo:hover .rs-orb {
  box-shadow: 0 0 0 2px rgba(6,182,212,0.65), 0 6px 28px rgba(99,102,241,0.48);
  transform: scale(1.08) rotate(-7deg);
}

.rs-logo-name {
  font-family: var(--rs-fs);
  font-size: 1.15rem; font-weight: 800; color: var(--rs-t1);
  letter-spacing: -0.02em; white-space: nowrap; line-height: 1;
}
.rs-logo-tag {
  font-family: var(--rs-fm);
  font-size: 0.5rem; font-weight: 500; color: var(--rs-t4);
  text-transform: uppercase; letter-spacing: 0.24em; margin-top: 4px; white-space: nowrap;
}

/* ── NAV ── */
.rs-nav {
  flex: 1; padding: 14px 10px;
  overflow-y: auto; overflow-x: hidden;
  position: relative; z-index: 2;
}
.rs-nav::-webkit-scrollbar { width: 0; }

.rs-sec-lbl {
  font-family: var(--rs-fm);
  font-size: 0.5rem; font-weight: 600;
  color: rgba(6,182,212,0.22);
  text-transform: uppercase; letter-spacing: 0.28em;
  padding: 0 12px; margin: 6px 0 8px; white-space: nowrap;
}
.rs-sep {
  height: 1px; margin: 8px 12px;
  background: linear-gradient(90deg, transparent, rgba(6,182,212,0.08), transparent);
}

/* ── NAV ITEM ── */
.rs-item {
  display: flex; align-items: center; gap: 11px;
  padding: 0 12px; height: 46px;
  border-radius: 12px; margin-bottom: 3px;
  text-decoration: none;
  font-family: var(--rs-fd); font-size: 0.9rem; font-weight: 500;
  color: var(--rs-t3);
  border: 1px solid transparent;
  position: relative; overflow: hidden; cursor: pointer; white-space: nowrap;
  transition: color 0.2s, background 0.2s, border-color 0.2s, box-shadow 0.2s;
}

/* Shimmer sweep on hover */
.rs-item::after {
  content: '';
  position: absolute; top: 0; left: -130%; width: 70%; bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(6,182,212,0.09), transparent);
  transform: skewX(-16deg);
  transition: left 0.5s var(--rs-out); pointer-events: none;
}
.rs-item:hover { color: var(--rs-t1); background: rgba(6,182,212,0.07); border-color: rgba(6,182,212,0.14); }
.rs-item:hover::after { left: 160%; }

/* Active state */
.rs-item.on {
  color: #fff;
  background: linear-gradient(90deg, rgba(6,182,212,0.17) 0%, rgba(99,102,241,0.06) 100%);
  border-color: rgba(6,182,212,0.22);
  box-shadow: inset 0 1px 0 rgba(6,182,212,0.15), 0 2px 20px rgba(6,182,212,0.15);
}
.rs-item.on::after { display: none; }

/* Active accent bar */
.rs-item.on::before {
  content: '';
  position: absolute; left: 0; top: 18%; bottom: 18%;
  width: 3px; border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, #06b6d4, #6366f1, #818cf8);
  box-shadow: 0 0 14px rgba(6,182,212,0.95), 0 0 6px rgba(99,102,241,0.5);
}

/* Icon box */
.rs-ico {
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.025);
  transition: background 0.22s, box-shadow 0.22s, transform 0.22s var(--rs-spring);
}
.rs-ico svg { width: 15px; height: 15px; }
.rs-item:hover .rs-ico {
  background: rgba(6,182,212,0.15);
  box-shadow: 0 0 12px rgba(6,182,212,0.2);
  transform: scale(1.1);
}
.rs-item.on .rs-ico {
  background: linear-gradient(135deg, rgba(6,182,212,0.3), rgba(99,102,241,0.12));
  box-shadow: 0 2px 14px rgba(6,182,212,0.25);
}
.rs-item.on .rs-ico svg { filter: drop-shadow(0 0 4px rgba(6,182,212,0.7)); }

.rs-item-txt { flex: 1; white-space: nowrap; letter-spacing: 0.005em; }

.rs-badge {
  font-family: var(--rs-fm);
  font-size: 0.5rem; font-weight: 600;
  padding: 2px 7px; border-radius: 99px;
  background: rgba(6,182,212,0.1); color: var(--rs-cyan4);
  border: 1px solid rgba(6,182,212,0.22);
  letter-spacing: 0.08em; text-transform: uppercase;
}

/* Tooltip (collapsed) */
.rs-tipwrap { position: relative; }
.rs-tip {
  position: absolute; left: calc(100% + 14px); top: 50%; transform: translateY(-50%);
  background: rgba(5,10,24,0.98); border: 1px solid var(--rs-b2);
  border-radius: 10px; padding: 6px 14px;
  font-family: var(--rs-fd); font-size: 0.82rem; font-weight: 600; color: var(--rs-t1);
  white-space: nowrap; pointer-events: none; opacity: 0;
  transition: opacity 0.18s; z-index: 100;
  box-shadow: 0 4px 20px rgba(0,0,0,0.55), 0 0 0 1px rgba(6,182,212,0.08);
}
.rs-tip::before {
  content: ''; position: absolute; left: -5px; top: 50%;
  width: 8px; height: 8px;
  background: rgba(5,10,24,0.98);
  border-left: 1px solid var(--rs-b2); border-bottom: 1px solid var(--rs-b2);
  transform: translateY(-50%) rotate(45deg);
}
.rs-tipwrap:hover .rs-tip { opacity: 1; }

/* ── RESELLER CARD ── */
.rs-rcard {
  margin: 0 10px 70px;
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(6,182,212,0.09), rgba(99,102,241,0.03));
  border: 1px solid rgba(6,182,212,0.12);
  position: relative; overflow: hidden; z-index: 2;
  transition: border-color 0.28s, box-shadow 0.28s, transform 0.28s;
}
.rs-rcard:hover {
  border-color: rgba(6,182,212,0.3);
  box-shadow: 0 4px 36px rgba(6,182,212,0.15);
  transform: translateY(-2px);
}
.rs-rcard::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(6,182,212,0.5), rgba(99,102,241,0.5), transparent);
}
.rs-rcard::after {
  content: ''; position: absolute; bottom: -32px; right: -32px;
  width: 120px; height: 120px; border-radius: 50%;
  background: radial-gradient(circle, rgba(99,102,241,0.1), transparent 70%);
  pointer-events: none;
}
.rs-rcard-in { padding: 16px; position: relative; z-index: 1; }
.rs-rcard-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.rs-rcard-av {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, rgba(6,182,212,0.3), rgba(99,102,241,0.14));
  border: 1px solid rgba(6,182,212,0.25);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 16px rgba(6,182,212,0.16);
  transition: box-shadow 0.25s, transform 0.25s var(--rs-spring);
  font-family: var(--rs-fs); font-size: 0.85rem; font-weight: 800; color: #06b6d4;
}
.rs-rcard:hover .rs-rcard-av { box-shadow: 0 0 28px rgba(6,182,212,0.34); transform: scale(1.07); }
.rs-rcard-lbl { font-family: var(--rs-fm); font-size: 0.5rem; font-weight: 600; color: rgba(6,182,212,0.44); text-transform: uppercase; letter-spacing: 0.2em; margin-bottom: 3px; }
.rs-rcard-name { font-family: var(--rs-fs); font-size: 0.9rem; font-weight: 700; color: var(--rs-t1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: -0.01em; }
.rs-rcard-div { height: 1px; background: linear-gradient(90deg, transparent, rgba(6,182,212,0.1), transparent); margin: 0 0 11px; }
.rs-rcard-bot { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.rs-rcard-id-lbl { font-family: var(--rs-fm); font-size: 0.54rem; font-weight: 600; color: var(--rs-t4); text-transform: uppercase; letter-spacing: 0.12em; }
.rs-rcard-id-val { font-family: var(--rs-fm); font-size: 0.65rem; color: rgba(6,182,212,0.6); background: rgba(6,182,212,0.06); border: 1px solid rgba(6,182,212,0.12); border-radius: 6px; padding: 2px 8px; }
.rs-rcard-status { display: flex; align-items: center; gap: 5px; font-family: var(--rs-fm); font-size: 0.62rem; font-weight: 600; color: var(--rs-emerald); }
.rs-rcard-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--rs-emerald); box-shadow: 0 0 10px rgba(52,211,153,0.85); animation: rcdot 2.4s ease-in-out infinite; }
@keyframes rcdot { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.6);opacity:0.45} }

/* ── COLLAPSE BUTTON ── */
.rs-togwrap {
  position: absolute; bottom: 20px; left: 0; right: 0;
  display: flex; justify-content: center; z-index: 6; pointer-events: none;
}
.rs-tog {
  pointer-events: all;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 18px; border-radius: 99px;
  background: rgba(6,182,212,0.07);
  border: 1px solid rgba(6,182,212,0.16);
  color: var(--rs-t3); cursor: pointer;
  position: relative; overflow: hidden;
  transition: color 0.22s, border-color 0.25s, box-shadow 0.25s, transform 0.22s, background 0.22s;
  white-space: nowrap; font-family: var(--rs-fd);
}
.rs-tog::before { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(6,182,212,0.07), transparent); opacity: 0; transition: opacity 0.25s; }
.rs-tog:hover {
  color: #06b6d4; border-color: rgba(6,182,212,0.52);
  box-shadow: 0 0 30px rgba(6,182,212,0.24), inset 0 0 16px rgba(6,182,212,0.04);
  transform: translateY(-2px) scale(1.04);
}
.rs-tog:hover::before { opacity: 1; }
.rs-tog svg { width: 13px; height: 13px; flex-shrink: 0; }
.rs-tog-txt { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; }
.rs-tog.pill { padding: 10px; border-radius: 50%; gap: 0; }
.rs-tog.pill .rs-tog-txt { display: none; }

/* ══ HEADER ══ */
.rs-header {
  height: 70px;
  background: rgba(4,8,16,0.82); backdrop-filter: blur(32px) saturate(180%); -webkit-backdrop-filter: blur(32px) saturate(180%);
  border-bottom: 1px solid rgba(6,182,212,0.07);
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 32px; position: sticky; top: 0; z-index: 30;
}
.rs-header::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(6,182,212,0.09),rgba(99,102,241,0.09),transparent); pointer-events: none; }

.rs-huser {
  display: flex; align-items: center; gap: 10px;
  background: rgba(6,182,212,0.04); border: 1px solid rgba(6,182,212,0.09);
  border-radius: 14px; padding: 7px 16px 7px 7px;
  cursor: pointer; color: inherit;
  transition: background 0.22s, border-color 0.22s, box-shadow 0.22s, transform 0.22s;
}
.rs-huser:hover { background: rgba(6,182,212,0.1); border-color: var(--rs-b2); box-shadow: 0 4px 22px rgba(6,182,212,0.2); transform: translateY(-2px); }
.rs-hav {
  width: 34px; height: 34px; border-radius: 50%;
  background: linear-gradient(135deg, #06b6d4, #6366f1);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 0 2px rgba(6,182,212,0.28), 0 3px 14px rgba(6,182,212,0.28);
  transition: box-shadow 0.25s;
  font-family: var(--rs-fs); font-size: 0.75rem; font-weight: 800; color: #fff;
}
.rs-huser:hover .rs-hav { box-shadow: 0 0 0 2.5px rgba(6,182,212,0.65), 0 4px 20px rgba(99,102,241,0.42); }
.rs-hname { font-family: var(--rs-fs); font-size: 0.88rem; font-weight: 700; color: var(--rs-t1); letter-spacing: -0.01em; }
.rs-hrole { font-family: var(--rs-fm); font-size: 0.62rem; color: var(--rs-t3); }

.rs-hbtn { width: 40px; height: 40px; border-radius: 11px; background: rgba(6,182,212,0.04); border: 1px solid rgba(6,182,212,0.09); display: flex; align-items: center; justify-content: center; color: var(--rs-t3); cursor: pointer; position: relative; transition: all 0.22s; }
.rs-hbtn:hover { background: rgba(6,182,212,0.12); border-color: var(--rs-b2); color: #22d3ee; box-shadow: 0 4px 18px rgba(6,182,212,0.2); transform: translateY(-2px) scale(1.07); }
.rs-hdot { position: absolute; top: 8px; right: 8px; width: 7px; height: 7px; border-radius: 50%; background: var(--rs-violet4); border: 1.5px solid var(--rs-n900); box-shadow: 0 0 10px var(--rs-violet4); animation: rshdot 2.6s ease-in-out infinite; }
@keyframes rshdot { 0%,80%,100%{transform:scale(1);opacity:1} 40%{transform:scale(1.7);opacity:0.4} }

/* Dropdown */
.rs-dd { background: var(--rs-n850)!important; border: 1px solid var(--rs-b2)!important; border-radius: 18px!important; box-shadow: 0 20px 60px rgba(0,0,0,0.82)!important; overflow: hidden; min-width: 220px; font-family: var(--rs-fd)!important; }
.rs-ddhd { padding: 16px 20px 13px; border-bottom: 1px solid rgba(6,182,212,0.07); }
.rs-ddi { padding: 11px 20px!important; font-size: 0.875rem!important; font-family: var(--rs-fd)!important; color: var(--rs-t2)!important; cursor: pointer!important; border-radius: 0!important; transition: background 0.15s,color 0.15s!important; display: flex!important; align-items: center!important; }
.rs-ddi:hover { background: rgba(6,182,212,0.08)!important; color: var(--rs-t1)!important; }
.rs-ddi.danger { color: var(--rs-rose)!important; }
.rs-ddi.danger:hover { background: rgba(251,113,133,0.09)!important; }

/* ══ MOBILE BAR ══ */
.rs-mobbar { position: fixed; top: 0; left: 0; right: 0; height: 62px; background: rgba(4,8,16,0.96); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border-bottom: 1px solid rgba(6,182,212,0.08); display: flex; align-items: center; justify-content: space-between; padding: 0 16px; z-index: 55; }
.rs-mobbtn { width: 40px; height: 40px; border-radius: 11px; background: rgba(6,182,212,0.06); border: 1px solid rgba(6,182,212,0.1); display: flex; align-items: center; justify-content: center; color: var(--rs-t3); cursor: pointer; transition: all 0.2s; }
.rs-mobbtn:hover { background: rgba(6,182,212,0.14); border-color: var(--rs-b2); color: var(--rs-cyan4); }
.rs-mobov { position: fixed; inset: 0; background: rgba(2,5,12,0.88); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); z-index: 48; }

/* ══ RESPONSIVE SHOW / HIDE via pure CSS ══ */
@media (min-width: 1024px) {
  .rs-mobbar       { display: none !important; }
  .rs-mob-sb       { display: none !important; }
  .rs-mobile-main  { display: none !important; }
  .rs-desktop-main { display: flex !important; }
}
@media (max-width: 1023px) {
  .rs-sb:not(.rs-mob-sb) { display: none !important; }
  .rs-desktop-main        { display: none !important; }
  .rs-mobbar              { display: flex !important; }
  .rs-mob-sb              { display: flex !important; }
  .rs-mobile-main         { display: block !important; }
}
.rs-rcard {
  position: relative;
  top: 3px;
}

/* ══ PAGE TRANSITION ══ */
.rs-page { animation: rsPgIn 0.42s cubic-bezier(0.22,1,0.36,1) both; }
@keyframes rsPgIn { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }

/* ══ PREVENT HORIZONTAL SCROLL ══ */
html, body { overflow-x: hidden; max-width: 100vw; }
.rs-main-wrap { overflow-x: hidden; min-width: 0; background: #080f1a; }
.rs-page-inner { overflow-x: hidden; min-width: 0; background: #080f1a; }
.rs-page-inner > * { box-sizing: border-box; max-width: 100%; }
`;

/* ── Inject CSS once ── */
let cssInjected = false;
function useGlobalCSS() {
  useEffect(() => {
    if (cssInjected) return;
    cssInjected = true;
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ── Nav item config ── */
const NAV_ITEMS = (t) => [
  { n: t("dashboard"),    I: LayoutDashboard, p: "ResellerDashboard" },
  { n: t("transactions"), I: Receipt,          p: "ResellerTransactions" },
  { n: t("deposits"),     I: ArrowDownToLine,  p: "ResellerDeposits" },
  { n: t("withdrawals"),  I: ArrowUpFromLine,  p: "ResellerWithdrawals" },
  { n: t("merchants"),    I: Building2,         p: "ResellerMerchants" },
  { n: t("providers"),    I: Server,            p: "ResellerProviders", badge: "VIEW" },
  { n: t("routing"),      I: Route,             p: "ResellerRouting" },
  { n: t("webhooks"),     I: Webhook,           p: "ResellerWebhooks" },
  { n: t("settlements"),  I: FileText,          p: "ResellerSettlements" },
  { n: t("notifications"),I: Bell,              p: "Notifications" },
  { n: t("settings"),     I: Settings,          p: "ResellerSettings" },
];

/* ── Get initials from name ── */
function getInitials(name) {
  if (!name) return "R";
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
}

/* ══════════════════════════════════════════
   SIDEBAR INNER — memo so it NEVER re-mounts
══════════════════════════════════════════ */
const SidebarInner = memo(function SidebarInner({
  col, isMobile, nav, currentPage, resellerName, resellerCode, t,
  onLogoClick, onNavClick, onToggle,
}) {
  const initials = getInitials(resellerName);

  return (
    <>
      {/* Logo */}
      <Link to={createPageUrl("ResellerDashboard")} onClick={onLogoClick} className="rs-logo">
        <div className="rs-orb"><Sparkles /></div>
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="lt"
              initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-10 }}
              transition={{ duration:0.2 }} style={{ overflow:"hidden" }}>
              <div className="rs-logo-name">1401</div>
              <div className="rs-logo-tag">{t("resellerPanel") || "Reseller Panel"}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </Link>

      {/* Nav */}
      <nav className="rs-nav">
        <AnimatePresence initial={false}>
          {(!col || isMobile) && (
            <motion.div key="lbl-main" className="rs-sec-lbl"
              initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
              Navigation
            </motion.div>
          )}
        </AnimatePresence>

        {nav.map((item) => {
          const active = currentPage === item.p;
          const isSettings = item.p === "ResellerSettings";
          return (
            <React.Fragment key={item.p}>
              {isSettings && (
                <>
                  <div className="rs-sep" />
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key="lbl-cfg" className="rs-sec-lbl"
                        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} transition={{ duration:0.15 }}>
                        Config
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}

              <div className={(col && !isMobile) ? "rs-tipwrap" : undefined}>
                <Link
                  to={createPageUrl(item.p)}
                  onClick={(e) => onNavClick(item.p, e)}
                  className={`rs-item${active ? " on" : ""}`}
                  style={(col && !isMobile) ? { justifyContent:"center", padding:"0", height:46, borderRadius:12 } : {}}
                >
                  <div className="rs-ico"><item.I /></div>
                  <AnimatePresence initial={false}>
                    {(!col || isMobile) && (
                      <motion.div key={`l-${item.p}`}
                        initial={{ opacity:0, width:0 }} animate={{ opacity:1, width:"auto" }} exit={{ opacity:0, width:0 }}
                        transition={{ duration:0.18 }}
                        style={{ display:"flex", alignItems:"center", gap:8, overflow:"hidden", flex:1 }}>
                        <span className="rs-item-txt">{item.n}</span>
                        {item.badge && <span className="rs-badge">{item.badge}</span>}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Link>
                {(col && !isMobile) && <div className="rs-tip">{item.n}{item.badge ? ` · ${item.badge}` : ""}</div>}
              </div>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Reseller Card */}
      <AnimatePresence initial={false}>
        {(!col || isMobile) && (
          <motion.div key="rcard"
            initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }}
            transition={{ duration:0.26 }} style={{ overflow:"hidden" }}>
            <div className="rs-rcard">
              <div className="rs-rcard-in">
                <div className="rs-rcard-top">
                  <div className="rs-rcard-av">{initials}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div className="rs-rcard-lbl">Logged in as</div>
                    <div className="rs-rcard-name">{resellerName}</div>
                  </div>
                </div>
                <div className="rs-rcard-div" />
                <div className="rs-rcard-bot">
                  {resellerCode
                    ? <><span className="rs-rcard-id-lbl">Code</span><span className="rs-rcard-id-val">{resellerCode}</span></>
                    : <><span className="rs-rcard-id-lbl">Role</span><span className="rs-rcard-id-val">Reseller</span></>}
                  <div className="rs-rcard-status"><div className="rs-rcard-dot" /><span>Active</span></div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapse button — desktop only */}
      {!isMobile && (
        <div className="rs-togwrap">
          <motion.button
            className={`rs-tog${col ? " pill" : ""}`}
            onClick={onToggle}
            whileTap={{ scale:0.92 }}
            title={col ? "Expand sidebar" : "Collapse sidebar"}
          >
            <motion.div animate={{ rotate: col ? 180 : 0 }} transition={{ duration:0.38, ease:[0.34,1.56,0.64,1] }}>
              <ChevronLeft size={14} />
            </motion.div>
            {!col && <span className="rs-tog-txt">Collapse</span>}
          </motion.button>
        </div>
      )}
    </>
  );
});

/* ══════════════════════════════════════════
   MAIN LAYOUT
══════════════════════════════════════════ */
export default function ResellerLayout({
  children, currentPage,
  resellerName = "Reseller Partner", resellerCode = "",
}) {
  useGlobalCSS();

  const [col,    setCol]    = useState(false);
  const [mob,    setMob]    = useState(false);
  const navigate            = useNavigate();
  const { t }               = useLanguage();
  const nav                 = NAV_ITEMS(t);
  const initials            = getInitials(resellerName);

  const logout = () => { auth.logout(); window.location.href = "/ResellerLogin"; };

  const handleNavClick = useCallback((targetPage, e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl(targetPage)), 320);
  }, [mob, navigate]);

  const handleLogoClick = useCallback((e) => {
    if (!mob) return;
    e.preventDefault();
    setMob(false);
    setTimeout(() => navigate(createPageUrl("ResellerDashboard")), 320);
  }, [mob, navigate]);

  const sidebarW = col ? 68 : 258;

  return (
    <div style={{ minHeight:"100vh", background:"var(--rs-n950)", overflowX:"hidden" }}>

      {/* ── MOBILE TOP BAR ── */}
      <div className="rs-mobbar">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button className="rs-mobbtn" onClick={()=>setMob(v=>!v)} aria-label="Toggle menu">
            <AnimatePresence mode="wait" initial={false}>
              {mob
                ? <motion.span key="x" initial={{ rotate:-90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:90,opacity:0 }} transition={{ duration:0.14 }}><X size={17}/></motion.span>
                : <motion.span key="m" initial={{ rotate:90,opacity:0 }} animate={{ rotate:0,opacity:1 }} exit={{ rotate:-90,opacity:0 }} transition={{ duration:0.14 }}><Menu size={17}/></motion.span>}
            </AnimatePresence>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div className="rs-orb" style={{ width:34, height:34, borderRadius:10 }}><Sparkles /></div>
            <span style={{ fontFamily:"var(--rs-fs)", fontWeight:800, fontSize:"1.05rem", color:"var(--rs-t1)" }}>1401</span>
          </div>
        </div>
        <div className="rs-hbtn" style={{ position:"relative" }}>
          <Bell size={17}/><div className="rs-hdot"/>
        </div>
      </div>

      {/* ── MOBILE OVERLAY ── */}
      <AnimatePresence>
        {mob && (
          <motion.div className="rs-mobov"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            transition={{ duration:0.2 }} onClick={()=>setMob(false)} />
        )}
      </AnimatePresence>

      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className="rs-sb"
        style={{
          width: sidebarW,
          transition: "width 0.38s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <SidebarInner
          col={col} isMobile={false}
          nav={nav} currentPage={currentPage}
          resellerName={resellerName} resellerCode={resellerCode}
          t={t}
          onLogoClick={handleLogoClick}
          onNavClick={handleNavClick}
          onToggle={() => setCol(v=>!v)}
        />
      </aside>

      {/* ── DESKTOP MAIN ── */}
      <div
        className="rs-main-wrap rs-desktop-main"
        style={{ marginLeft:sidebarW, transition:"margin-left 0.38s cubic-bezier(0.22,1,0.36,1)", minHeight:"100vh", display:"flex", flexDirection:"column", overflowX:"hidden" }}
      >
        <header className="rs-header">
          <div style={{ flex:1 }} />
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="violet" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rs-huser">
                  <div className="rs-hav">{initials}</div>
                  <div style={{ textAlign:"left" }}>
                    <div className="rs-hname">{resellerName}</div>
                    <div className="rs-hrole">{t("reseller") || "Reseller"}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rs-dd">
                <div className="rs-ddhd">
                  <div style={{ fontFamily:"var(--rs-fs)", fontWeight:700, color:"var(--rs-t1)", fontSize:"0.92rem" }}>{resellerName}</div>
                  <div style={{ fontFamily:"var(--rs-fm)", fontSize:"0.65rem", color:"var(--rs-t4)", marginTop:4 }}>{resellerCode || "Reseller"}</div>
                </div>
                <DropdownMenuItem className="rs-ddi" onClick={()=>navigate(createPageUrl("ResellerSettings"))}>
                  <Settings style={{ width:14, height:14, marginRight:10, opacity:0.5 }} />{t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator style={{ background:"rgba(6,182,212,0.07)" }} />
                <DropdownMenuItem className="rs-ddi danger" onClick={logout}>
                  <LogOut style={{ width:14, height:14, marginRight:10 }} />{t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main key={currentPage} className="rs-page rs-page-inner" style={{ flex:1, padding:"28px 32px 60px", overflowX:"hidden" }}>
          {children}
        </main>
      </div>

      {/* ── MOBILE SIDEBAR ── */}
      <aside
        className="rs-sb rs-mob-sb"
        style={{
          width: 270,
          transform: mob ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.32s cubic-bezier(0.22,1,0.36,1)",
          zIndex: 52,
        }}
      >
        <SidebarInner
          col={false} isMobile={true}
          nav={nav} currentPage={currentPage}
          resellerName={resellerName} resellerCode={resellerCode}
          t={t}
          onLogoClick={handleLogoClick}
          onNavClick={handleNavClick}
          onToggle={() => setMob(false)}
        />
      </aside>

      {/* ── MOBILE MAIN ── */}
      <div
        className="rs-main-wrap rs-mobile-main"
        key={currentPage}
        style={{ paddingTop:62, overflowX:"hidden" }}
      >
        <div className="rs-page rs-page-inner" style={{ padding:"16px 14px 60px", overflowX:"hidden" }}>
          {children}
        </div>
      </div>
    </div>
  );
}